import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  getOperatorsScripts,
  getOrderResponse,
  getSelectedExhibitionId,
  getSelectedStep,
  selectAreScriptsLoaded,
  selectIsMaintenanceEnabled,
  selectScripts
} from '@app/app.reducer';
import { AppService } from '@app/app.service';
import { HeaderScriptType, ScriptInjectId, StepNameToScriptType } from '@core/data-access/models/script-injector.model';
import { InitializationService } from '@core/data-access/models/service.model';
import { ScriptsAPI } from '@core/data-access/services/scripts/scripts-api';
import { RouterActions } from '@core/data-access/store/router/router.actions';
import { selectRouteNestedParams } from '@core/data-access/store/router/router.selectors';
import { hasInjectedScript, injectScriptElement, toStepScriptId } from '@core/utils/dom-helpers';
import { hasScript } from '@core/utils/predicates';
import { select, Store } from '@ngrx/store';
import { getAllBookedProductsTotalPrice } from '@store/products/booking/product/product-booking.selectors';
import { selectRedeemedValidDiscountPromoCodes } from '@store/products/booking/smart-promotion/booking-smart-promotion.selectors';
import { every, forEach, isEmpty, isEqual, isNil, isNull, toNumber } from 'lodash';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  first,
  iif,
  map,
  Observable,
  of,
  pairwise,
  shareReplay,
  switchMap,
  withLatestFrom
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ScriptInjectionManager implements InitializationService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #document = inject(DOCUMENT);
  readonly #scriptsAPI = inject(ScriptsAPI);
  readonly #appService = inject(AppService);

  readonly #eventId$ = this.setEventId$();

  init() {
    this.initOperatorScriptsInjectionSubscription();
    this.initAllPagesHeaderScriptInjectionSubscription();
    this.initSelectedStepScriptInjectionSubscription();
  }

  reloadScriptsOnEventSelectionCheck$() {
    return (navigation: Observable<ReturnType<typeof RouterActions.navigateToEventSelection>>) =>
      navigation.pipe(
        switchMap(() => this.#store.select(getSelectedExhibitionId)),
        pairwise(),
        filter(([_, currentEventId]) => isNull(currentEventId)),
        switchMap(() => this.#store.pipe(select(selectScripts), first())),
        map((scripts) => !every(scripts, isEmpty))
      );
  }

  reloadScriptsOnSelectEventCheck$() {
    return (eventId: Observable<ReturnType<typeof RouterActions.navigateToSelectedEvent>>) =>
      eventId.pipe(
        switchMap(({ eventId }) =>
          this.#store.pipe(
            select(selectScripts),
            first(),
            switchMap((scripts) =>
              iif(
                () => isNull(scripts),
                of({ eventId, reloadScripts: false }),
                this.#scriptsAPI.getEventScripts$(eventId).pipe(
                  map((scriptsResponse) => ({
                    eventId,
                    reloadScripts: !isEqual(scripts, scriptsResponse)
                  }))
                )
              )
            )
          )
        )
      );
  }

  private initOperatorScriptsInjectionSubscription() {
    this.whenRendered$(this.#store.select(getOperatorsScripts))
      .pipe(
        first((operatorsScripts) => !isEmpty(operatorsScripts)),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((operatorsScripts) => {
        forEach(operatorsScripts, (script, index) =>
          injectScriptElement(this.#document, script, `${ScriptInjectId.OperatorsScript}-${index}`)
        );
      });
  }

  private initAllPagesHeaderScriptInjectionSubscription() {
    this.whenRendered$(
      combineLatest({
        eventId: this.#eventId$,
        areScriptsLoaded: this.#store.select(selectAreScriptsLoaded)
      })
    )
      .pipe(
        first(({ areScriptsLoaded }) => areScriptsLoaded),
        this.filterIsEventMaintenanceDisabled$(),
        switchMap(() =>
          this.#store.pipe(
            select(selectScripts),
            first(),
            filter((scripts) => hasScript(scripts, HeaderScriptType.AllPagesHeader))
          )
        ),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((scripts) =>
        injectScriptElement(
          this.#document,
          scripts[HeaderScriptType.AllPagesHeader],
          ScriptInjectId.EventAllPagesScript
        )
      );
  }

  private initSelectedStepScriptInjectionSubscription() {
    const selectedStepScript$ = this.#store.pipe(
      select(getSelectedStep),
      map((selectedStep) => StepNameToScriptType[selectedStep]),
      filter((scriptType) => !isNil(scriptType)),
      map((scriptType) => ({ scriptId: toStepScriptId(scriptType), scriptType }))
    );

    this.whenRendered$(
      combineLatest({
        eventId: this.#eventId$,
        selectedStepScript: selectedStepScript$,
        areScriptsLoaded: this.#store.select(selectAreScriptsLoaded)
      })
    )
      .pipe(
        filter(
          ({ selectedStepScript: { scriptId }, areScriptsLoaded }) =>
            areScriptsLoaded && !hasInjectedScript(this.#document, scriptId)
        ),
        this.filterIsEventMaintenanceDisabled$(),
        withLatestFrom(
          this.#store.select(selectScripts),
          this.#store.select(getAllBookedProductsTotalPrice),
          this.#store.select(selectRedeemedValidDiscountPromoCodes),
          this.#store.select(getOrderResponse)
        ),
        filter(
          ([
            {
              selectedStepScript: { scriptType }
            },
            scripts
          ]) => hasScript(scripts, scriptType)
        ),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(
        ([
          {
            selectedStepScript: { scriptId, scriptType }
          },
          scripts,
          { totalPrice, totalDiscountedPrice },
          redeemedValidDiscountPromoCodes,
          orderResponse
        ]) => {
          const totalTicketPrice = isEmpty(redeemedValidDiscountPromoCodes) ? totalPrice : totalDiscountedPrice;
          const orderId = orderResponse?.szCode?.toString() ?? (Math.random() * 1e12).toFixed(0).toString();

          injectScriptElement(this.#document, scripts[scriptType], scriptId, totalTicketPrice.toString(), orderId);
        }
      );
  }

  private setEventId$() {
    return combineLatest({
      routeNestedParams: this.#store.select(selectRouteNestedParams),
      selectedExhibitionId: this.#store.select(getSelectedExhibitionId)
    }).pipe(
      map(({ routeNestedParams: { id }, selectedExhibitionId }) => ({ id, selectedExhibitionId })),
      first(({ id, selectedExhibitionId }) => toNumber(id) === selectedExhibitionId),
      map(({ id }) => id),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }

  private whenRendered$<T>(source$: Observable<T>) {
    return this.#appService.isRendered$.pipe(switchMap(() => source$));
  }

  private filterIsEventMaintenanceDisabled$<T extends { eventId: string | null }>() {
    return (source$: Observable<T>) =>
      source$.pipe(
        filter(({ eventId }) => !isNil(eventId)),
        switchMap((value) =>
          this.#store.pipe(
            select(selectIsMaintenanceEnabled(toNumber(value.eventId))),
            first(),
            filter((isMaintenanceEnabled) => !isMaintenanceEnabled),
            map(() => value)
          )
        )
      );
  }
}
